import React, { useCallback, useMemo } from 'react';
import _ from 'lodash';
import {
  ReflectionProperty,
  ReflectionKind,
  TypeEnum,
  TypeArray,
  ReflectionClass,
} from '@deepkit/type';
import { twMerge } from 'tailwind-merge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useV3TraitsStore } from 'react-scale/corp/views/V3TraitsStores/V3TraitsStore';
import {
  DeliveryHubMenu,
  GenAIOpsHubPaths,
  MainSidebarMenu,
} from 'react-scale/corp/views/CorpProjectGroupManager/constants';
import StringInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/StringInputBuilder';
import {
  TextArea,
  Tooltip,
  SelectMenu,
  Autocomplete,
  BaseIcon,
  Icon,
  Button,
  Typography,
} from '@scale/scaleui';
import NumberInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/NumberInputBuilder';
import BooleanInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/BooleanInputBuilder';
import JSONInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/JSONInputBuilder';
import * as traitDefinitions from '@scale/temporal-workflows/task-pipeline/actions/index';
import {
  PipelineTrait,
  actionMap,
} from '@scale/temporal-workflows/task-pipeline/framework/manual-intervention/pipeline-action';
import { ReviewLevel, ReviewLevelOrder } from '@scale/shared-types';
import { Text } from 'design-system/Typography';

const pipelineTraits = Object.values(traitDefinitions) as PipelineTrait[];
export interface ISelectedPipeline {
  id: string;
  pipeline: string;
  traits: string[];
}

interface Props {
  selectedTrait: string;
  setSelectedTrait: (trait: string) => void;
  traitParamValues: Record<string, any>;
  updateTraitParams: (value: any, name: string) => void;
  clearTraitParams: () => void;
  selectedPipeline?: ISelectedPipeline;
  project?: { label: string; value: string };
}

const TaskPipelineTraitSelector: React.FC<Props> = ({
  selectedTrait,
  setSelectedTrait,
  traitParamValues,
  updateTraitParams,
  clearTraitParams,
  selectedPipeline,
  project,
}) => {
  const mostUsedTraits = useV3TraitsStore(s => s.mostUsedTraits);

  const traitParamProperties = actionMap.get(selectedTrait)?.params
    ? ReflectionClass.from(actionMap.get(selectedTrait)!.params).getProperties()
    : [];

  const pipelineTrait: PipelineTrait | undefined = pipelineTraits.find(
    trait => trait.operatorName === selectedTrait,
  );

  const traits = (Object.values(traitDefinitions) as PipelineTrait[]).sort((lhs, rhs) =>
    lhs.operatorName.localeCompare(rhs.operatorName),
  );

  const getComponentForEnum = ({
    reflectionProperty,
    isMulti,
    currentValue,
    propertyMetadata,
    label,
  }: {
    reflectionProperty: ReflectionProperty;
    isMulti: boolean;
    currentValue: string | string[];
    propertyMetadata?: { title: string; hint: string; warning?: string };
    label: string;
  }) => {
    const values = isMulti
      ? ((reflectionProperty.type as TypeArray).type as TypeEnum).values
      : (reflectionProperty.type as TypeEnum).values;
    return (
      <SelectMenu
        label={label}
        multiple={isMulti as any} // TODO fix scaleui types to be able to send boolean
        helperText={propertyMetadata?.hint}
        options={
          values
            .filter(x => {
              if (_.isNil(x)) {
                return false;
              }
              if (_.every(ReviewLevelOrder, r => values.includes(r))) {
                return (ReviewLevelOrder as readonly ReviewLevel[]).includes(x as ReviewLevel);
              }
              return true;
            })
            .map(value => ({
              id: value,
              name: value,
            })) as { id: string; name: string }[]
        }
        setSelected={(idOrIds: any) => {
          updateTraitParams(idOrIds, reflectionProperty.name);
        }}
        selectedOptionId={isMulti ? currentValue ?? [] : currentValue}
      />
    );
  };

  // TODO move this to another file and review each field
  const getComponentFromReflectionProperty = (
    reflectionProperty: ReflectionProperty,
    propertyMetadata?: { title: string; hint: string; warning?: string },
    required?: boolean,
    hide?: boolean,
  ) => {
    if (hide) {
      return null;
    }

    const name = (required ? '* ' : '') + (propertyMetadata?.title ?? reflectionProperty.name);
    switch (reflectionProperty.type.kind) {
      case ReflectionKind.enum: {
        return getComponentForEnum({
          reflectionProperty,
          isMulti: false,
          currentValue: traitParamValues[reflectionProperty.name],
          propertyMetadata,
          label: name,
        });
      }
      case ReflectionKind.number: {
        return (
          <NumberInputBuilder
            hint={propertyMetadata?.hint}
            name={name}
            updateProperty={(e: any) => updateTraitParams(e, reflectionProperty.name)}
            existingValue={traitParamValues[reflectionProperty.name]}
          />
        );
      }
      case ReflectionKind.string: {
        return (
          <StringInputBuilder
            hint={propertyMetadata?.hint}
            name={name}
            updateProperty={(e: any) => updateTraitParams(e, reflectionProperty.name)}
            existingValue={traitParamValues[reflectionProperty.name]}
          />
        );
      }
      case ReflectionKind.boolean: {
        return (
          <BooleanInputBuilder
            hint={propertyMetadata?.hint}
            name={name}
            updateProperty={(e: any) => updateTraitParams(e, reflectionProperty.name)}
            existingValue={traitParamValues[reflectionProperty.name]}
          />
        );
      }
      case ReflectionKind.array: {
        switch (reflectionProperty.type.type.kind) {
          case ReflectionKind.enum: {
            return getComponentForEnum({
              reflectionProperty,
              isMulti: true,
              currentValue: traitParamValues[reflectionProperty.name],
              propertyMetadata,
              label: name,
            });
          }
          default: {
            return (
              <>
                <Text medium>{name}</Text>
                <JSONInputBuilder
                  name=""
                  hint={propertyMetadata?.hint}
                  updateProperty={value => {
                    updateTraitParams(value, reflectionProperty.name);
                  }}
                  required={true}
                />
              </>
            );
          }
        }
      }
      case ReflectionKind.objectLiteral:
        // TODO: Make this recursive for more complex trait param inputs
        return (
          <>
            <Text medium>{name}</Text>
            <JSONInputBuilder
              hint={propertyMetadata?.hint}
              name=""
              updateProperty={e => {
                updateTraitParams(e, reflectionProperty.name);
              }}
            />
          </>
        );
      case ReflectionKind.any: {
        return (
          <TextArea
            id={reflectionProperty.name}
            label={name}
            helperText={propertyMetadata?.hint}
            onChange={e => {
              updateTraitParams(e.target.value, reflectionProperty.name);
            }}
          />
        );
      }
    }
  };

  const {
    actionTraitOptions,
    actionTraitsByName,
    mostUsedTraitNames,
    actionTraitsFromStartCaseToOriginalValue,
  }: {
    actionTraitOptions: string[];
    actionTraitsByName: Record<string, PipelineTrait>;
    mostUsedTraitNames: string[];
    actionTraitsFromStartCaseToOriginalValue: Record<string, string>;
  } = useMemo(() => {
    const aux = traits.filter(trait => {
      if (selectedPipeline) {
        return selectedPipeline.traits.includes(trait.operatorName);
      }
      return true;
    });
    // camelCase the trait names with some exceptions like QIR
    const actionTraitOptions = _.sortBy(
      aux.map(x => x.operatorName),
      // Using - to avoid reversing the array afterwards
      x => -mostUsedTraits[x] || 1,
    );

    // This is not pretty but as Autocomplete only receives a string[] we need to do it this way
    const actionTraitsFromStartCaseToOriginalValue = actionTraitOptions.reduce(
      (acc, x) => ({
        ...acc,
        [_.startCase(x)]: x,
      }),
      {},
    );

    return {
      actionTraitOptions,
      mostUsedTraitNames: actionTraitOptions.slice(0, 5),
      actionTraitsByName: _.keyBy(aux, 'operatorName'),
      actionTraitsFromStartCaseToOriginalValue,
    };
  }, [selectedPipeline, traits, mostUsedTraits]);

  const goToDeliverySender = useCallback(() => {
    const projectId = project?.value ?? 'YOUR_PROJECT_ID';
    const path = `${GenAIOpsHubPaths.Projects}/${projectId}/${MainSidebarMenu.DeliveryHub}/${DeliveryHubMenu.DeliverySender}`;

    window.open(path, '_blank');
  }, [project?.value]);

  return (
    <div className="items-start justify-start w-full rounded-lg text-neutral-500 flex flex-col gap-2">
      {selectedTrait === 'sendTaskFromResponse' && (
        // Fork of scaleui banner with a tooltip on the button
        <div className="mb-2 pr-3 flex items-center justify-between gap-3 bg-neutral-50 w-full transition-colors rounded-lg scaleui text-neutral-900 h-fit overflow-hidden">
          <div className="relative flex items-center h-full">
            <div className="w-1 h-full rounded-l absolute bg-danger-500" />
            <div className="ml-3">
              <Icon icon="exclamation-circle" variant={'danger'} />{' '}
            </div>
            <div className="py-3 ml-3">
              <p className="text-sm font-medium">
                GenAI (non-OTS) projects must use Delivery Sender to complete Tasks.
              </p>
              <p className="text-xs font-normal">
                Contact #genai-delivery-hub if you have any questions
              </p>
            </div>
          </div>
          <div className="flex items-center h-full space-x-2">
            <Tooltip
              text="You must edit the URL when this opens"
              disabled={!!project?.value}
              asChild={false}
              delayDuration={0}
              target={
                <Button
                  className="w-[150px] justify-center text-center"
                  variant="neutral"
                  onClick={goToDeliverySender}
                >
                  {project?.value ? 'Go to Delivery Sender' : 'Open URL Template'}
                </Button>
              }
            />
          </div>
        </div>
      )}

      <Typography.Subtitle3>Action to apply to all the found tasks</Typography.Subtitle3>
      <div className="flex gap-2 flex-row items-center">
        <Autocomplete
          canCreateOption={false}
          className="w-fit"
          options={actionTraitOptions.map(_.startCase)}
          setSelectedOption={opt => {
            if (opt !== selectedTrait) {
              setSelectedTrait(actionTraitsFromStartCaseToOriginalValue[opt]);
              clearTraitParams();
            }
          }}
          inputProps={{ className: 'w-72' }}
          renderOption={(option: string, selected: boolean) => {
            const trait: PipelineTrait =
              actionTraitsByName[actionTraitsFromStartCaseToOriginalValue[option]];
            return (
              <Tooltip
                text={<span className="max-w-sm whitespace-normal">{trait.description}</span>}
                side="right"
                target={
                  <div className="flex gap-1 flex-row items-center mr-2">
                    {mostUsedTraitNames.includes(
                      actionTraitsFromStartCaseToOriginalValue[option],
                    ) && (
                      <span>
                        <FontAwesomeIcon icon="star" className="text-warning-600" />
                      </span>
                    )}
                    <p
                      className={twMerge(
                        selected ? 'font-semibold' : 'font-normal',
                        'block truncate origin-left transition-transform',
                      )}
                    >
                      {option}
                    </p>
                    <span>
                      <FontAwesomeIcon icon="info-circle" />
                    </span>
                  </div>
                }
              />
            );
          }}
          value={_.startCase(selectedTrait)}
        />
        {selectedTrait ? (
          <div className="flex-row justify-end">
            <Tooltip
              text={
                <span className="max-w-sm whitespace-normal">
                  {actionTraitsByName[selectedTrait]?.description}
                </span>
              }
              target={<FontAwesomeIcon size="lg" icon="info-circle" />}
            />
          </div>
        ) : null}
      </div>

      {/* TODO make it so nested props have padding so it's easy to see which one is related to which one */}
      {selectedTrait &&
        traitParamProperties &&
        traitParamProperties.map(reflectionProperty => {
          const propertyMetadata =
            actionTraitsByName[selectedTrait]?.paramMeta[reflectionProperty.name];
          const required = !reflectionProperty.property.optional;
          return (
            <span
              className="flex flex-col gap-1 w-full max-w-md"
              key={('reflection-' + (reflectionProperty?.property?.name as string)) as string}
            >
              {getComponentFromReflectionProperty(
                reflectionProperty,
                propertyMetadata,
                required,
                pipelineTrait?.entityType === (reflectionProperty?.property?.name as string),
              )}
              {propertyMetadata?.warning && (
                <div className="flex flex-row gap-0.5 items-center justify-start ml-4">
                  <BaseIcon icon="exclamation-circle" variant={'warning'} />
                  <Typography.Overline3 className="text-warning-800 whitespace-normal">
                    {propertyMetadata?.warning}
                  </Typography.Overline3>
                </div>
              )}
            </span>
          );
        })}
    </div>
  );
};

export default TaskPipelineTraitSelector;
